/**
 * Collections Tab - Quản lý các bộ sưu tập tài liệu
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Tooltip,
  Popconfirm,
  Tag,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import * as aiAssistantService from '../../services/aiAssistantService';

const CollectionsTab: React.FC = () => {
  const [collections, setCollections] = useState<aiAssistantService.AICollection[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCollection, setEditingCollection] = useState<aiAssistantService.AICollection | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const data = await aiAssistantService.listCollections();
      setCollections(data);
    } catch (error) {
      message.error('Không thể tải danh sách bộ sưu tập');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCollection(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: aiAssistantService.AICollection) => {
    setEditingCollection(record);
    form.setFieldsValue({
      name: record.Name,
      description: record.Description,
      icon: record.Icon,
      color: record.Color,
    });
    setModalVisible(true);
  };

  const handleDelete = async (collectionId: number) => {
    try {
      await aiAssistantService.deleteCollection(collectionId);
      message.success('Xóa bộ sưu tập thành công');
      loadCollections();
    } catch (error) {
      message.error('Không thể xóa bộ sưu tập');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCollection) {
        await aiAssistantService.updateCollection(editingCollection.CollectionID, values);
        message.success('Cập nhật bộ sưu tập thành công');
      } else {
        await aiAssistantService.createCollection(values);
        message.success('Tạo bộ sưu tập thành công');
      }
      
      setModalVisible(false);
      loadCollections();
    } catch (error) {
      message.error('Có lỗi xảy ra');
      console.error(error);
    }
  };

  

  const columns: ColumnsType<aiAssistantService.AICollection> = [
    {
      title: 'Icon',
      dataIndex: 'Icon',
      key: 'icon',
      width: 60,
      render: (icon: string) => (
        icon ? <span style={{ fontSize: '24px' }}>{icon}</span> : <FolderOutlined style={{ fontSize: '24px' }} />
      ),
    },
    {
      title: 'Tên bộ sưu tập',
      dataIndex: 'Name',
      key: 'name',
      render: (name: string, record: aiAssistantService.AICollection) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.Description && (
            <div style={{ fontSize: '12px', color: '#888' }}>{record.Description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'File Search Store',
      dataIndex: 'FileSearchStoreName',
      key: 'storeName',
      render: (storeName: string) => <Tag color="blue">{storeName}</Tag>,
    },
    {
      title: 'Số tài liệu',
      dataIndex: 'DocumentCount',
      key: 'documentCount',
      align: 'center',
      width: 120,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CreatedAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: aiAssistantService.AICollection) => (
        <Space>
            <Tooltip title="Đồng bộ lại toàn bộ tài liệu trong bộ sưu tập dưới API key hiện tại">
              <Button type="default" onClick={() => handleResyncCollection(record.CollectionID)}>
                Resync dưới API key
              </Button>
            </Tooltip>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Xác nhận xóa?"
            description="Tất cả tài liệu trong bộ sưu tập sẽ bị xóa"
            onConfirm={() => handleDelete(record.CollectionID)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Resync handler is inside component so it can access loadCollections
  const handleResyncCollection = async (collectionId: number) => {
    try {
      message.loading({ content: 'Đang đồng bộ lại bộ sưu tập...', key: 'resync', duration: 0 });
      const result = await aiAssistantService.resyncCollection(collectionId);
      message.destroy('resync');
      if (result && result.success) {
        message.success('Re-sync hoàn tất. Kiểm tra log chi tiết để biết kết quả từng tài liệu.');
        loadCollections();
      } else {
        message.error(result.message || 'Re-sync thất bại');
      }
    } catch (error) {
      message.destroy('resync');
      message.error('Lỗi khi re-sync bộ sưu tập');
      console.error('Re-sync error:', error);
    }
  };
  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Tạo bộ sưu tập mới
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={collections}
        rowKey="CollectionID"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingCollection ? 'Sửa bộ sưu tập' : 'Tạo bộ sưu tập mới'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        okText="Lưu"
        cancelText="Hủy"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Tên bộ sưu tập"
            rules={[{ required: true, message: 'Vui lòng nhập tên bộ sưu tập' }]}
          >
            <Input placeholder="Ví dụ: Nội quy trường học" />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} placeholder="Mô tả ngắn về bộ sưu tập" />
          </Form.Item>

          <Form.Item name="icon" label="Icon (Emoji)">
            <Input placeholder="📚" maxLength={2} />
          </Form.Item>

          <Form.Item name="color" label="Màu sắc (Hex)">
            <Input placeholder="#1890ff" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
  

export default CollectionsTab;
